
import React, { useContext, useEffect, useRef, useState } from "react";
import { CardScheme, Config } from "types/creditCard.types";
import { FormattedMessage, IntlContext, useIntl } from "react-intl";
import { Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { PBLResponseContext, useUuidPblData } from "../../UUIDContexted.tsx";
import { useTheme } from "../../context/ThemeContext.tsx";
import { useParams, useNavigate } from "react-router-dom";
import { getPBLData, submitClickToPayPaymentRequest, submitCreditCardPaymentRequest } from "../../api/apiService.ts";
import PaylaDcs from '../PaylaDsc.tsx';
import { PaymentProcessingLoader } from "../PaymentProcessingLoader.tsx";
import PaymentNotSupported from "../PaymentNotSupported.tsx";
import { v4 as uuidv4 } from 'uuid';
import { ctpConfig, darkModeUIConfig, defaultUIConfig, getExpFromJwt } from "./creditcard.helper.ts";
import CONSTANTS from "../../utils/constants.js";

const cards: Record<string, string> = {
    "1": "visa",
    "2": "amex",
    "3": "mastercard",
    "125": "jcb",
    "132": "diners",
};

const paymentMethodMap: Record<string, number> = {
    VISA: 1,
    AMERICAN_EXPRESS: 2,
    MASTERCARD: 3,
    JCB: 125,
    DINERS: 132,
};

const localeMap: Record<string, string> = {
    "en": "en_US",
    "de": "de_DE"
};

const CreditCard: React.FC = () => {
    const intl = useIntl();
    const { theme } = useTheme();
    const pblData = useUuidPblData();

    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();

    const { setPblResponseData } = useContext(PBLResponseContext);
    const switchLocale = React.useContext(IntlContext);

    const iframeContainerRef = useRef<HTMLDivElement>(null);
    const postRequestInFlightRef = useRef(false);
    const snippetToken = useRef("");
    const timeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [termsError, setTermsError] = useState("");
    const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
    const [validCardDataEntered, setValidCardDataEntered] = useState(false);
    const [token, setToken] = useState(pblData.token);
    const [submitTriggered, setSubmitTriggered] = useState(false);

    const allowedCardSchemes = pblData?.paymentMethods.map((method) => cards[method]).filter(Boolean) as CardScheme[];
    const enableCTP = pblData.paymentMethods.includes("5002") ? true : false;
    let locale = intl.locale;
    const uiConfig = ctpConfig.enableCTP ? defaultUIConfig : theme === "dark" ? darkModeUIConfig : defaultUIConfig;

    const tokenizationSuccess = async (
        _statusCode: string,
        token: string,
        cardDetails: Record<string, string>,
        cardInputMode: string
    ) => {
        if (!token || !cardDetails || !cardInputMode) return;
        if (postRequestInFlightRef.current) return;

        const paymentMethodId = (cardInputMode === "clickToPay" || cardInputMode === "register") 
            ? 5002 : paymentMethodMap[cardDetails.cardType];

        postRequestInFlightRef.current = true;

        try {
            const response =
                (cardInputMode === "clickToPay" || cardInputMode === "register")
                    ? await submitClickToPayPaymentRequest(pblData.linkId, paymentMethodId, {
                        network: cardDetails.cardType,
                        paymentCheckoutData: token,
                    })
                    : await submitCreditCardPaymentRequest(pblData.linkId, token, paymentMethodId, snippetToken.current);

            const action = response?.createPaymentResponse?.merchantAction;

            if (response.linkId === pblData?.linkId && action?.actionType === "REDIRECT") {
                navigate(action.redirectData.redirectURL);
            }
        } catch (error) {
            setPblResponseData({ error, linkId: uuid });
        } finally {
            postRequestInFlightRef.current = false;
            setShowPaymentProcessing(false);
        }
    };

    const tokenizationfailed = (_statusCode: string, errorResponse: object) => {
        setPblResponseData({ error: errorResponse, linkId: uuid });
        setShowPaymentProcessing(false);
    };

    const callbackFunction = (status: string, statusCode: number) => {
        if (status === "InvalidCardDataEntered") {
            setValidCardDataEntered(false);
        } else if (status === "ValidCardDataEntered") {
            setValidCardDataEntered(true);
        }
        if (statusCode >= 200 && statusCode <= 299) {

        } else if (statusCode >= 400 && statusCode <= 599) {
            setIframeLoading(false);
            console.error("Error in Hosted Tokenization Page:", status);
        }
    }

    useEffect(() => {
        if (!token) return;
    
        const expiry = getExpFromJwt(token);

        // adding 100ms for handeling time skews
        const refreshInMs = Math.max(expiry * 1000 - Date.now() + 100, 0);

        timeOutRef.current = setTimeout(async () => {
            const response = await getPBLData(uuid!);
            setToken(response.data.token);
        }, refreshInMs);

        return () => {
            if (timeOutRef.current) clearTimeout(timeOutRef.current);
        }
    }, [token]);

    useEffect(() => {
        const partnerMerchantId = pblData ? pblData.bnplId : '';
        snippetToken.current = `${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`;
        setPblResponseData(null);
    }, []);

    useEffect(() => {
        locale = localeMap[locale.split('-')[0]] || "en_US";
        const mode = window.location.origin === CONSTANTS.PROD_URL ? 'live' : 'test';
        const CTPConfig = {
            ...ctpConfig,
            enableCTP,
            transactionAmount: pblData.amountOfMoney,
        };

        const config: Config = {
            iframe: {
                iframeWrapperId: 'payment-IFrame',
                width: 980,
                zIndex: 1,
            },
            uiConfig,
            locale: locale,
            mode: mode,
            token: token,
            allowedCardSchemes: allowedCardSchemes,
            ...(enableCTP && { CTPConfig }),
        };

        const init = async () => {
            await window.HostedTokenizationSdk.init();
            window.HostedTokenizationSdk.getPaymentPage(config, callbackFunction);
        };

        if (isPaymentMethodAvailable) {
            setIframeLoading(true);
            init();
        }
    }, [isPaymentMethodAvailable, locale, uiConfig, token]);

    // adding event listener for listening messages from the hosted tokenization page
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            switch (event.data) {
                case "validationPass":
                    setPblResponseData(null);
                    if (termsAgreed) {
                        setShowPaymentProcessing(true);
                    }
                    break;

                default:
                    if (event.data.card) {

                    } else if (event.data.error) {
                        console.error(`${event.data.error}, Status code: ${event.data.statusCode}`);
                    } else if (event.data.errors) {
                        Object.values(event.data.errors)
                            .flatMap(errors => errors)
                            .forEach(error => console.error(`${error}, Status code: ${event.data.statusCode}`));
                    }
            }
        }

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        }
    }, [termsAgreed]);

    useEffect(() => {
        if (
            pblData &&
            pblData.paymentMethods &&
            pblData.paymentMethods.length > 0 &&
            pblData.token
        ) {
            let isPaymentMethodAvailable = pblData.paymentMethods.some(id => ["1", "2", "3", "125", "132"].includes(id));
            setIsPaymentMethodAvailable(isPaymentMethodAvailable);
        }
    }, [pblData]);

    // as the wrapper doesn't allow us to pass a callback fuction, we are using a MutationObserver on
    // the "payment-IFrame" div to monitor if the iframe is successfullly rendered or not
    useEffect(() => {
        if (!iframeLoading) return;

        const container = iframeContainerRef.current;
        if (!container) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    const iframe = (node as HTMLElement).querySelector('iframe');
                    if (iframe) {
                        iframe.addEventListener("load", () => {
                            setIframeLoading(false);
                            observer.disconnect();
                        });
                    }
                });
            });
        });

        observer.observe(container, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    });

    // workaround for now to submit the form when ctp is not enabled.
    // have to rework on this onch triggerValidation is implemented 
    // for onboarding form.
    useEffect(() => {
        if (validCardDataEntered && termsAgreed && !enableCTP) {
            window.HostedTokenizationSdk.submitForm(tokenizationSuccess, tokenizationfailed);
            setShowPaymentProcessing(true);
        }
    }, [validCardDataEntered, submitTriggered]);

    useEffect(() => {
      setTermsError("");
    }, [switchLocale?.locale]);

    const handleSubmit = () => {
        if (!termsAgreed) {
            setTermsError(CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED);
        }

        if (enableCTP) {
            if (termsAgreed) {
                window.HostedTokenizationSdk.submitForm(tokenizationSuccess, tokenizationfailed);
            }
            return;
        }

        window.HostedTokenizationSdk.triggerValidations(callbackFunction);
        setSubmitTriggered(!submitTriggered);
    }

    if (!isPaymentMethodAvailable) {
        return <PaymentNotSupported />
    }

    return (
        <>
            <Container fluid className="creditcard-payment g-0" data-testid="pm-creditcard-payment">
                <Row>
                    <Col className="d-flex flex-column">
                        <h5 className="section-title" data-testid="pm-text">
                            <FormattedMessage
                                id={CONSTANTS.CREDITCARD_PAGE.HEADING}
                                defaultMessage="Credit Card"
                            />
                        </h5>
                        <span className="section-subtitle volte-medium" data-testid="pm-text">
                            <FormattedMessage
                                id={CONSTANTS.CREDITCARD_PAGE.SUB_HEADING}
                                defaultMessage="Please enter your credit card information"
                            />
                        </span>
                        {iframeLoading &&
                            <Spinner
                                animation="border"
                                role="status"
                                className="main-spinner align-self-center m-3"
                                style={{ color: "var(--PAYONE-BLAU, #0096D6)" }}
                            >
                                <span className="visually-hidden"><FormattedMessage id={CONSTANTS.LOADING.LOADING_TEXT} defaultMessage="Loading..." /></span>
                            </Spinner>
                        }
                        <div
                            className={ctpConfig.enableCTP ? "pt-3" : ""}
                            id="payment-IFrame"
                            ref={iframeContainerRef}
                            style={{
                                display: iframeLoading ? "none" : "block",
                                backgroundColor: ctpConfig.enableCTP ? "#ffffff" : ""
                            }}
                        />
                    </Col>
                </Row>
                <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
                    <h5 data-testid="pm-label-terms-payment" className="section-title">
                        {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
                    </h5>
                    <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
                        <Form.Check.Input
                            type="checkbox"
                            className="terms-checkbox-input m-0"
                            checked={termsAgreed}
                            onChange={(e) => {
                                setTermsAgreed(e.target.checked);
                                setTermsError("");
                            }}
                            data-testid="pm-terms-checkbox-input"
                        />
                        <label className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                            <FormattedMessage
                                id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                                defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                            />
                        </label>
                    </div>
                    {!termsAgreed && termsError !== "" && (
                        <span id="not-agree" className="error_message" data-testid="pm-not-agree">
                            <FormattedMessage
                                id={CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED}
                                defaultMessage="Please agree to our terms and conditions before submiting"
                            />
                        </span>
                    )}
                </Row>
                <Row>
                    {showPaymentProcessing ? (
                        <Col className={`align-self-start`}>
                            <PaymentProcessingLoader />
                        </Col>
                    ) : (
                        <Col className="text-end">
                            <button
                                id="submit"
                                className="btn btn-primary text-white px-4 rounded-4 volte-medium"
                                onClick={handleSubmit}
                                data-testid="pm-submit-btn"
                                disabled={iframeLoading}
                            >
                                <FormattedMessage
                                    id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT}
                                    defaultMessage="Complete payment"
                                />
                            </button>
                        </Col>
                    )}
                </Row>
                <PaylaDcs snippetToken={snippetToken.current} agreed={termsAgreed} />
            </Container>
        </>
    );
}

export default CreditCard;